import { Box, Skeleton, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { ThreatDetails } from '../ThreatCard/ThreatCard.shared.style';
import {
  HorizontalLine,
  MaliciousText,
  NoMaliciousText,
  SuspiciousText,
  UnknownText,
  BenignText,
} from '../../pages/ThreadsDetails/Style/SingleThreat.style';
import { SectionColumn, SectionTitle, InfoRow, InfoLabel } from '../ThreatTable/ThreatExpandedRow.style';
import { formatedDate } from '../../utils';
import ResurgentThreatIcon from '../ResurgentThreatIcon/ResurgentThreatIcon';

type PreCrimeLevel = 'malicious' | 'suspicious' | 'unknown' | 'benign';

const PRE_CRIME_COMPONENTS: Record<PreCrimeLevel, React.ReactNode> = {
  malicious: <MaliciousText>Malicious</MaliciousText>,
  suspicious: <SuspiciousText>Suspicious</SuspiciousText>,
  unknown: <UnknownText>Unknown</UnknownText>,
  benign: <BenignText>Benign</BenignText>,
};

interface ExpandedSummaryProps {
  status: string | undefined;
  isLoading: boolean;
  preCrimeLevel: PreCrimeLevel;
  gsbThreatTypes: string[];
  gsbLoading: boolean;
  dnsARecord: string;
  dnsLoading: boolean;
  resurgence?: {
    isResurgent: boolean;
    date: string | undefined;
  };
  detailUrl: string;
  takedownDetailUrl?: string;
  onLinkClick?: (e: React.MouseEvent) => void;
}

const ExpandedSummary = ({
  status,
  isLoading,
  preCrimeLevel,
  gsbThreatTypes,
  gsbLoading,
  dnsARecord,
  dnsLoading,
  resurgence,
  detailUrl,
  takedownDetailUrl,
  onLinkClick,
}: ExpandedSummaryProps) => (
  <SectionColumn>
    <SectionTitle>Summary</SectionTitle>
    <HorizontalLine sx={{ mb: 1 }} />

    <InfoRow>
      <InfoLabel>Status:</InfoLabel>
      <Typography variant="body2">
        {isLoading ? <Skeleton variant="text" width={80} /> : status || 'N/A'}
      </Typography>
    </InfoRow>

    <InfoRow>
      <InfoLabel>PreCrime Network:</InfoLabel>
      <Typography variant="body2" component="span">
        {isLoading ? (
          <Skeleton variant="text" width={80} />
        ) : status === 'confirmed' ? (
          PRE_CRIME_COMPONENTS[preCrimeLevel]
        ) : (
          <NoMaliciousText>No Classification</NoMaliciousText>
        )}
      </Typography>
    </InfoRow>

    <InfoRow>
      <InfoLabel>Google Safe Browsing:</InfoLabel>
      <Typography variant="body2" component="span">
        {gsbLoading ? (
          <Skeleton variant="text" width={80} />
        ) : gsbThreatTypes.length > 0 ? (
          <MaliciousText>Malicious ({gsbThreatTypes.join(', ')})</MaliciousText>
        ) : (
          <NoMaliciousText>No Classification</NoMaliciousText>
        )}
      </Typography>
    </InfoRow>

    <InfoRow>
      <InfoLabel>DNS A Record:</InfoLabel>
      <Typography variant="body2">
        {dnsLoading ? <Skeleton variant="text" width={80} /> : dnsARecord}
      </Typography>
    </InfoRow>

    {resurgence?.isResurgent && (
      <InfoRow>
        <InfoLabel sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <ResurgentThreatIcon size={14} sx={{ color: 'warning.main' }} />
          Resurgence Confirmed:
        </InfoLabel>
        <Typography variant="body2">
          {resurgence.date ? formatedDate(resurgence.date) : 'N/A'}
        </Typography>
      </InfoRow>
    )}

    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <ThreatDetails
        to={detailUrl}
        target="_blank"
        onClick={onLinkClick}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          fontSize: '13px',
        }}>
        View Full Threat Details
        <OpenInNewIcon sx={{ fontSize: 14 }} />
      </ThreatDetails>
      {takedownDetailUrl && (
        <ThreatDetails
          to={takedownDetailUrl}
          target="_blank"
          onClick={onLinkClick}
          data-testid="view-takedown-details-link"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '13px',
          }}>
          View Takedown Details
          <OpenInNewIcon sx={{ fontSize: 14 }} />
        </ThreatDetails>
      )}
    </Box>
  </SectionColumn>
);

export default ExpandedSummary;
